import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';
import SimulatorPage from './SimulatorPage';
import AnalyticsCharts from '@/components/AnalyticsCharts';

/* ─── Types ────────────────────────────────────── */
type TabId = 'overview' | 'users' | 'boxes' | 'ledger' | 'fund' | 'maintenance' | 'rules' | 'referral' | 'subscriptions' | 'simulator' | 'charts' | 'i18n' | 'system' | 'notifications' | 'chat' | 'gamification' | 'activity';

const tabs: { id: TabId; label: string; icon: string }[] = [
  { id: 'overview',       label: 'Panoramica',    icon: '📊' },
  { id: 'users',          label: 'Utenti',        icon: '👥' },
  { id: 'boxes',          label: 'Box Monitor',   icon: '📦' },
  { id: 'ledger',         label: 'Ledger',        icon: '💶' },
  { id: 'fund',           label: 'Fondo',         icon: '🏦' },
  { id: 'maintenance',    label: 'Manutenzione',  icon: '🔧' },
  { id: 'rules',          label: 'Regole',        icon: '📐' },
  { id: 'referral',       label: 'Referral',      icon: '🤝' },
  { id: 'subscriptions',  label: 'Abbonamenti',   icon: '💳' },
  { id: 'simulator',      label: 'Simulatore',    icon: '🎲' },
  { id: 'charts',         label: 'Grafici',       icon: '📈' },
  { id: 'i18n',           label: 'Traduzioni',    icon: '🌐' },
  { id: 'system',         label: 'Sistema',       icon: '⚡' },
  { id: 'notifications',  label: 'Notifiche',     icon: '🔔' },
  { id: 'chat',           label: 'Chat',          icon: '💬' },
  { id: 'gamification',   label: 'Gamification',  icon: '🏆' },
  { id: 'activity',       label: 'Attività',      icon: '📋' },
];

/* ─── AdminPage ────────────────────────────────── */
export default function AdminPage() {
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  return (
    <>
      <Helmet><title>Admin — React-City</title></Helmet>

      <div className="space-y-6 animate-in">
        <h1 className="text-heading text-2xl font-bold">Pannello Admin</h1>

        {/* Tab bar */}
        <div className="flex gap-1 overflow-x-auto pb-2 scrollbar-hide">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === t.id
                  ? 'bg-primary-50 text-primary-700 dark:bg-primary-500/10 dark:text-primary-400'
                  : 'text-surface-500 hover:text-surface-700 hover:bg-surface-50 dark:text-surface-400 dark:hover:text-white dark:hover:bg-surface-800'
              }`}
            >
              <span>{t.icon}</span>{t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'overview'       && <OverviewTab />}
        {activeTab === 'users'          && <UsersTab />}
        {activeTab === 'boxes'          && <BoxesTab />}
        {activeTab === 'ledger'         && <LedgerTab />}
        {activeTab === 'fund'           && <FundTab />}
        {activeTab === 'maintenance'    && <MaintenanceTab />}
        {activeTab === 'rules'          && <RulesTab />}
        {activeTab === 'referral'       && <AdminReferralTab />}
        {activeTab === 'subscriptions'  && <AdminSubscriptionsTab />}
        {activeTab === 'simulator'      && <AdminSimulatorTab />}
        {activeTab === 'charts'         && <AdminChartsTab />}
        {activeTab === 'i18n'           && <AdminI18nTab />}
        {activeTab === 'system'         && <AdminSystemTab />}
        {activeTab === 'notifications'  && <AdminNotificationsTab />}
        {activeTab === 'chat'           && <AdminChatTab />}
        {activeTab === 'gamification'   && <AdminGamificationTab />}
        {activeTab === 'activity'       && <AdminActivityTab />}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════
   OVERVIEW TAB
   ═══════════════════════════════════════════════ */
function OverviewTab() {
  const { data, isLoading } = useQuery({ queryKey: ['admin-dashboard'], queryFn: () => api.get('/admin/dashboard').then(r => r.data) });
  const { data: alerts } = useQuery({ queryKey: ['admin-alerts'], queryFn: () => api.get('/admin/alerts').then(r => r.data) });
  const { data: metrics } = useQuery({ queryKey: ['admin-metrics'], queryFn: () => api.get('/admin/metrics?days=30').then(r => r.data) });

  if (isLoading) return <LoadingSpinner />;

  const kpis = [
    { label: 'Utenti attivi',    value: data?.total_users,        icon: '👥' },
    { label: 'Box totali',       value: data?.total_boxes,        icon: '📦' },
    { label: 'Box attivi',       value: data?.active_boxes,       icon: '🟢' },
    { label: 'Box completati',   value: data?.completed_boxes,    icon: '✅' },
    { label: 'Box merged',       value: data?.merged_boxes,       icon: '🔀' },
    { label: 'Fondo (€)',        value: data?.fund_balance?.toFixed(2),  icon: '🏦' },
    { label: 'Fee (€)',          value: data?.fee_balance?.toFixed(2),   icon: '💰' },
    { label: 'Volume totale (€)', value: data?.total_volume?.toFixed(2), icon: '📈' },
    { label: 'Join settimanali', value: data?.weekly_joins,       icon: '📅' },
    { label: 'Completati 7gg',   value: data?.weekly_completed,   icon: '🎯' },
    { label: 'Avg giorni compl.', value: data?.avg_completion_days, icon: '⏱️' },
    { label: 'Membri totali',   value: data?.total_memberships,  icon: '🤝' },
  ];

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {alerts?.alerts?.length > 0 && (
        <div className="space-y-2">
          {alerts.alerts.map((a: { level: string; message: string; type: string }, i: number) => (
            <div key={i} className={`card px-4 py-3 border-l-4 ${
              a.level === 'critical' ? 'border-red-500 bg-red-500/10' : 'border-yellow-500 bg-yellow-500/10'
            }`}>
              <span className="font-medium">{a.level === 'critical' ? '🚨' : '⚠️'} {a.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        {kpis.map((k) => (
          <div key={k.label} className="card p-4 text-center">
            <div className="text-2xl mb-1">{k.icon}</div>
            <div className="text-xl font-bold text-heading">{k.value ?? '—'}</div>
            <div className="text-xs text-muted">{k.label}</div>
          </div>
        ))}
      </div>

      {/* Distribuzione box */}
      {metrics?.box_distribution && (
        <div className="card p-5">
          <h3 className="text-heading font-semibold mb-3">Distribuzione Box</h3>
          <div className="flex flex-wrap gap-3">
            {metrics.box_distribution.map((b: { status: string; count: number }) => (
              <div key={b.status} className="bg-surface-100 dark:bg-surface-800 rounded-lg px-4 py-2">
                <span className="font-mono text-sm">{b.status}</span>
                <span className="ml-2 font-bold">{b.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Audit recente */}
      {data?.recent_audit?.length > 0 && (
        <div className="card p-5">
          <h3 className="text-heading font-semibold mb-3">Attività recente</h3>
          <div className="space-y-2">
            {data.recent_audit.map((a: { action: string; entity_type: string; created_at: string; actor_email: string }, i: number) => (
              <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-surface-100 dark:border-surface-800 last:border-0">
                <span className="text-muted">{a.actor_email ?? '—'}</span>
                <span className="font-mono text-xs">{a.action}</span>
                <span className="text-muted text-xs">{new Date(a.created_at).toLocaleString('it-IT')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   USERS TAB
   ═══════════════════════════════════════════════ */
function UsersTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin-users'], queryFn: () => api.get('/admin/users').then(r => r.data) });

  const updateUser = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, string> }) => api.patch(`/admin/users/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-50 dark:bg-surface-800 text-left">
              <th className="px-4 py-3 font-medium text-muted">Email</th>
              <th className="px-4 py-3 font-medium text-muted">Nome</th>
              <th className="px-4 py-3 font-medium text-muted">Ruolo</th>
              <th className="px-4 py-3 font-medium text-muted">Stato</th>
              <th className="px-4 py-3 font-medium text-muted">Registrato</th>
              <th className="px-4 py-3 font-medium text-muted">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {(data?.users ?? []).map((u: { id: string; email: string; full_name: string; role: string; status: string; created_at: string }) => (
              <tr key={u.id} className="border-t border-surface-100 dark:border-surface-800">
                <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3">{u.full_name}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    u.role === 'super_admin' ? 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400'
                    : u.role === 'admin' ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400'
                    : 'bg-surface-100 text-surface-600 dark:bg-surface-700 dark:text-surface-300'
                  }`}>{u.role}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    u.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400'
                    : u.status === 'suspended' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400'
                    : 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400'
                  }`}>{u.status}</span>
                </td>
                <td className="px-4 py-3 text-xs text-muted">{new Date(u.created_at).toLocaleDateString('it-IT')}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {u.status === 'active' && (
                      <button onClick={() => updateUser.mutate({ id: u.id, body: { status: 'suspended' }})} className="px-2 py-1 text-xs rounded-lg bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400 hover:opacity-80">
                        Sospendi
                      </button>
                    )}
                    {u.status === 'suspended' && (
                      <button onClick={() => updateUser.mutate({ id: u.id, body: { status: 'active' }})} className="px-2 py-1 text-xs rounded-lg bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400 hover:opacity-80">
                        Riattiva
                      </button>
                    )}
                    {u.status !== 'banned' && (
                      <button onClick={() => updateUser.mutate({ id: u.id, body: { status: 'banned' }})} className="px-2 py-1 text-xs rounded-lg bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400 hover:opacity-80">
                        Ban
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   BOXES TAB
   ═══════════════════════════════════════════════ */
function BoxesTab() {
  const { data, isLoading } = useQuery({ queryKey: ['admin-boxes-monitor'], queryFn: () => api.get('/admin/boxes/monitor').then(r => r.data) });

  if (isLoading) return <LoadingSpinner />;

  const healthColor = (h: string) => {
    switch (h) {
      case 'almost_full': return 'text-blue-500';
      case 'stale':       return 'text-red-500';
      case 'healthy':     return 'text-green-500';
      case 'CLOSED':      return 'text-surface-400';
      default:            return 'text-yellow-500';
    }
  };

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-50 dark:bg-surface-800 text-left">
              <th className="px-4 py-3 font-medium text-muted">ID</th>
              <th className="px-4 py-3 font-medium text-muted">Stato</th>
              <th className="px-4 py-3 font-medium text-muted">Health</th>
              <th className="px-4 py-3 font-medium text-muted">Fill %</th>
              <th className="px-4 py-3 font-medium text-muted">Slot</th>
              <th className="px-4 py-3 font-medium text-muted">Membri</th>
              <th className="px-4 py-3 font-medium text-muted">Giorni</th>
            </tr>
          </thead>
          <tbody>
            {(data?.boxes ?? []).map((b: { id: string; status: string; health: string; fill_pct: number; current_slots: number; max_slots: number; member_count: number; days_since_creation: number }) => (
              <tr key={b.id} className="border-t border-surface-100 dark:border-surface-800">
                <td className="px-4 py-3 font-mono text-xs">{b.id.slice(0, 8)}...</td>
                <td className="px-4 py-3 font-mono text-xs">{b.status}</td>
                <td className={`px-4 py-3 font-medium ${healthColor(b.health)}`}>{b.health}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 rounded-full bg-surface-200 dark:bg-surface-700">
                      <div className="h-full rounded-full bg-primary-500" style={{ width: `${Math.min(100, b.fill_pct)}%` }} />
                    </div>
                    <span className="text-xs">{b.fill_pct}%</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-xs">{b.current_slots}/{b.max_slots}</td>
                <td className="px-4 py-3 text-xs">{b.member_count}</td>
                <td className="px-4 py-3 text-xs">{b.days_since_creation}gg</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   LEDGER TAB
   ═══════════════════════════════════════════════ */
function LedgerTab() {
  const [filters, setFilters] = useState({ account_type: '', entry_type: '', min_amount: '', page: 1 });
  const queryKey = ['admin-ledger', filters];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => api.get('/admin/ledger/search', { params: filters }).then(r => r.data),
  });

  const { data: recon } = useQuery({
    queryKey: ['admin-reconciliation'],
    queryFn: () => api.get('/admin/reconciliation').then(r => r.data),
  });

  const handleExport = () => {
    window.open('/api/v1/admin/ledger/export', '_blank');
  };

  return (
    <div className="space-y-4">
      {/* Reconciliation card */}
      {recon?.double_entry && (
        <div className={`card p-4 border-l-4 ${recon.double_entry.balanced ? 'border-green-500' : 'border-red-500'}`}>
          <div className="flex items-center justify-between">
            <div>
              <span className="font-medium text-heading">Partita doppia: </span>
              <span className={recon.double_entry.balanced ? 'text-green-500' : 'text-red-500'}>
                {recon.double_entry.balanced ? '✅ Bilanciata' : '❌ Sbilanciata'}
              </span>
            </div>
            <div className="text-sm text-muted">
              Debiti: €{recon.double_entry.debit_total.toFixed(2)} | Crediti: €{recon.double_entry.credit_total.toFixed(2)}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs text-muted block mb-1">Tipo account</label>
            <select value={filters.account_type} onChange={e => setFilters(f => ({ ...f, account_type: e.target.value, page: 1 }))} className="input-field text-sm">
              <option value="">Tutti</option>
              <option value="user">User</option>
              <option value="box">Box</option>
              <option value="fund">Fund</option>
              <option value="fee">Fee</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-muted block mb-1">Tipo entry</label>
            <select value={filters.entry_type} onChange={e => setFilters(f => ({ ...f, entry_type: e.target.value, page: 1 }))} className="input-field text-sm">
              <option value="">Tutti</option>
              <option value="DEBIT">Debit</option>
              <option value="CREDIT">Credit</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-muted block mb-1">Importo min (€)</label>
            <input type="number" value={filters.min_amount} onChange={e => setFilters(f => ({ ...f, min_amount: e.target.value, page: 1 }))} className="input-field text-sm w-28" placeholder="0" />
          </div>
          <button onClick={handleExport} className="btn-secondary btn-sm">📥 Esporta CSV</button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? <LoadingSpinner /> : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-50 dark:bg-surface-800 text-left">
                  <th className="px-4 py-3 font-medium text-muted">Tipo</th>
                  <th className="px-4 py-3 font-medium text-muted">Importo</th>
                  <th className="px-4 py-3 font-medium text-muted">Descrizione</th>
                  <th className="px-4 py-3 font-medium text-muted">Account</th>
                  <th className="px-4 py-3 font-medium text-muted">Data</th>
                </tr>
              </thead>
              <tbody>
                {(data?.entries ?? []).map((e: { id: string; entry_type: string; amount: number; description: string; owner_type: string; created_at: string }) => (
                  <tr key={e.id} className="border-t border-surface-100 dark:border-surface-800">
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        e.entry_type === 'DEBIT' ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400'
                        : 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400'
                      }`}>{e.entry_type}</span>
                    </td>
                    <td className="px-4 py-3 font-mono">€{Number(e.amount).toFixed(2)}</td>
                    <td className="px-4 py-3 text-muted text-xs max-w-xs truncate">{e.description}</td>
                    <td className="px-4 py-3 text-xs font-mono">{e.owner_type}</td>
                    <td className="px-4 py-3 text-xs text-muted">{new Date(e.created_at).toLocaleString('it-IT')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data && data.total_pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-surface-100 dark:border-surface-800">
              <span className="text-xs text-muted">{data.total} risultati — Pagina {data.page}/{data.total_pages}</span>
              <div className="flex gap-2">
                <button onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))} disabled={filters.page <= 1} className="btn-ghost btn-sm">← Prec</button>
                <button onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))} disabled={filters.page >= data.total_pages} className="btn-ghost btn-sm">Succ →</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   FUND TAB
   ═══════════════════════════════════════════════ */
function FundTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin-fund'], queryFn: () => api.get('/fund/status').then(r => r.data) });
  const { data: history } = useQuery({ queryKey: ['admin-fund-history'], queryFn: () => api.get('/fund/history').then(r => r.data) });

  const checkThresholds = useMutation({
    mutationFn: () => api.post('/fund/check'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-fund'] }),
  });

  if (isLoading) return <LoadingSpinner />;
  const fund = data;

  const levelColor = (lvl: string) => {
    switch (lvl) {
      case 'HEALTHY':  return 'text-green-500';
      case 'NORMAL':   return 'text-blue-500';
      case 'LOW':      return 'text-yellow-500';
      case 'CRITICAL': return 'text-red-500';
      default:         return 'text-surface-400';
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card p-5 text-center">
          <div className="text-3xl font-bold text-heading">€{fund?.balance?.toFixed(2) ?? '0.00'}</div>
          <div className="text-sm text-muted mt-1">Saldo Fondo</div>
        </div>
        <div className="card p-5 text-center">
          <div className={`text-3xl font-bold ${levelColor(fund?.level)}`}>{fund?.level ?? '?'}</div>
          <div className="text-sm text-muted mt-1">Livello</div>
        </div>
        <div className="card p-5 text-center">
          <button onClick={() => checkThresholds.mutate()} disabled={checkThresholds.isPending} className="btn-primary w-full">
            {checkThresholds.isPending ? 'Controllo...' : '🔍 Verifica Soglie'}
          </button>
        </div>
      </div>

      {/* Soglie */}
      <div className="card p-5">
        <h3 className="text-heading font-semibold mb-3">Soglie configurate</h3>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div><div className="text-red-500 font-bold">€{fund?.threshold_critical ?? 50}</div><div className="text-xs text-muted">Critica</div></div>
          <div><div className="text-yellow-500 font-bold">€{fund?.threshold_minimum ?? 100}</div><div className="text-xs text-muted">Minima</div></div>
          <div><div className="text-green-500 font-bold">€{fund?.threshold_warning ?? 500}</div><div className="text-xs text-muted">Warning</div></div>
        </div>
      </div>

      {/* History */}
      {history?.entries?.length > 0 && (
        <div className="card p-5">
          <h3 className="text-heading font-semibold mb-3">Storico Fondo</h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {history.entries.map((e: { id: string; entry_type: string; amount: number; description: string; created_at: string }) => (
              <div key={e.id} className="flex items-center justify-between text-sm py-1.5 border-b border-surface-100 dark:border-surface-800 last:border-0">
                <span className={e.entry_type === 'CREDIT' ? 'text-green-500' : 'text-red-500'}>
                  {e.entry_type === 'CREDIT' ? '+' : '-'}€{Number(e.amount).toFixed(2)}
                </span>
                <span className="text-muted text-xs flex-1 mx-3 truncate">{e.description}</span>
                <span className="text-muted text-xs">{new Date(e.created_at).toLocaleString('it-IT')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAINTENANCE TAB
   ═══════════════════════════════════════════════ */
function MaintenanceTab() {
  const qc = useQueryClient();
  const { data: health, isLoading } = useQuery({ queryKey: ['admin-health'], queryFn: () => api.get('/maintenance/health').then(r => r.data) });
  const { data: queue } = useQuery({ queryKey: ['admin-queue'], queryFn: () => api.get('/maintenance/queue').then(r => r.data) });
  const { data: stale } = useQuery({ queryKey: ['admin-stale'], queryFn: () => api.get('/maintenance/stale').then(r => r.data) });
  const { data: bridge } = useQuery({ queryKey: ['admin-bridge'], queryFn: () => api.get('/maintenance/bridge-priority').then(r => r.data) });

  const runMaint = useMutation({
    mutationFn: () => api.post('/maintenance/run'),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-health'] }); qc.invalidateQueries({ queryKey: ['admin-stale'] }); },
  });

  const processQueue = useMutation({
    mutationFn: () => api.post('/maintenance/process'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-queue'] }),
  });

  if (isLoading) return <LoadingSpinner />;
  const h = health;

  const systemColor = (l: string) => {
    switch (l) {
      case 'HEALTHY':  return 'text-green-500';
      case 'CAUTION':  return 'text-yellow-500';
      case 'SLOWDOWN': return 'text-orange-500';
      case 'CRITICAL': return 'text-red-500';
      default:         return 'text-surface-400';
    }
  };

  return (
    <div className="space-y-4">
      {/* System health */}
      <div className="grid sm:grid-cols-4 gap-4">
        <div className="card p-4 text-center">
          <div className={`text-2xl font-bold ${systemColor(h?.status)}`}>{h?.status ?? '?'}</div>
          <div className="text-xs text-muted mt-1">Sistema</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-heading">{h?.stale_boxes ?? 0}</div>
          <div className="text-xs text-muted mt-1">Box stagnanti</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-heading">{h?.metrics?.weekly_joins ?? 0}</div>
          <div className="text-xs text-muted mt-1">Join/settimana</div>
        </div>
        <div className="card p-4 flex flex-col gap-2">
          <button onClick={() => runMaint.mutate()} disabled={runMaint.isPending} className="btn-primary btn-sm flex-1">
            {runMaint.isPending ? '⏳' : '🔧'} Manutenzione
          </button>
          <button onClick={() => processQueue.mutate()} disabled={processQueue.isPending} className="btn-secondary btn-sm flex-1">
            {processQueue.isPending ? '⏳' : '⚙️'} Processa coda
          </button>
        </div>
      </div>

      {/* Queue status */}
      {queue && (
        <div className="card p-5">
          <h3 className="text-heading font-semibold mb-3">Coda Job</h3>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div><div className="font-bold text-heading">{queue.total ?? 0}</div><div className="text-xs text-muted">Totale</div></div>
            <div><div className="font-bold text-heading">{queue.statuses?.length ?? 0}</div><div className="text-xs text-muted">Per tipo</div></div>
            <div><div className="font-bold text-heading">{queue.pending_by_type?.length ?? 0}</div><div className="text-xs text-muted">Pending</div></div>
          </div>
        </div>
      )}

      {/* Stale boxes */}
      {stale?.stale_boxes?.length > 0 && (
        <div className="card p-5">
          <h3 className="text-heading font-semibold mb-3">Box Stagnanti ({stale.stale_boxes.length})</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {stale.stale_boxes.map((b: { id: string; current_slots: number; max_slots: number; days_inactive: number }) => (
              <div key={b.id} className="flex items-center justify-between text-sm py-1.5 border-b border-surface-100 dark:border-surface-800 last:border-0">
                <span className="font-mono text-xs">{b.id.slice(0, 8)}...</span>
                <span>{b.current_slots}/{b.max_slots} slot</span>
                <span className="text-red-500">{b.days_inactive}gg inattivo</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bridge priority */}
      {bridge?.bridge_queue?.length > 0 && (
        <div className="card p-5">
          <h3 className="text-heading font-semibold mb-3">Priorità Bridge</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {bridge.bridge_queue.map((b: { user_id: string; user_email: string; waiting_days: number; priority_score: number }, i: number) => (
              <div key={b.user_id} className="flex items-center justify-between text-sm py-1.5 border-b border-surface-100 dark:border-surface-800 last:border-0">
                <span className="font-medium">#{i + 1}</span>
                <span className="text-xs">{b.user_email}</span>
                <span className="text-muted text-xs">{b.waiting_days}gg attesa</span>
                <span className="font-mono text-xs font-bold">{b.priority_score}pt</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   RULES TAB
   ═══════════════════════════════════════════════ */
function RulesTab() {
  const qc = useQueryClient();
  const [newRule, setNewRule] = useState({ rule_key: '', rule_value: '', description: '' });
  const [historyKey, setHistoryKey] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['admin-rules'], queryFn: () => api.get('/admin/rules').then(r => r.data) });
  const { data: ruleHist } = useQuery({
    queryKey: ['admin-rule-history', historyKey],
    queryFn: () => api.get(`/admin/rules/history/${historyKey}`).then(r => r.data),
    enabled: !!historyKey,
  });

  const createRule = useMutation({
    mutationFn: (body: typeof newRule) => api.post('/admin/rules', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-rules'] }); setNewRule({ rule_key: '', rule_value: '', description: '' }); },
  });

  const rollback = useMutation({
    mutationFn: ({ key, version }: { key: string; version: number }) => api.post(`/admin/rules/rollback/${key}/${version}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-rules'] }); qc.invalidateQueries({ queryKey: ['admin-rule-history', historyKey] }); },
  });

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      {/* Create rule */}
      <div className="card p-5">
        <h3 className="text-heading font-semibold mb-3">Crea / Aggiorna regola</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <input value={newRule.rule_key} onChange={e => setNewRule(r => ({ ...r, rule_key: e.target.value }))} placeholder="Chiave regola" className="input-field text-sm" />
          <input value={newRule.rule_value} onChange={e => setNewRule(r => ({ ...r, rule_value: e.target.value }))} placeholder="Valore" className="input-field text-sm" />
          <input value={newRule.description} onChange={e => setNewRule(r => ({ ...r, description: e.target.value }))} placeholder="Descrizione" className="input-field text-sm" />
        </div>
        <button onClick={() => createRule.mutate(newRule)} disabled={!newRule.rule_key || createRule.isPending} className="btn-primary btn-sm mt-3">
          {createRule.isPending ? 'Salvataggio...' : '➕ Salva regola'}
        </button>
      </div>

      {/* Active rules list */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-50 dark:bg-surface-800 text-left">
                <th className="px-4 py-3 font-medium text-muted">Chiave</th>
                <th className="px-4 py-3 font-medium text-muted">Valore</th>
                <th className="px-4 py-3 font-medium text-muted">Ver.</th>
                <th className="px-4 py-3 font-medium text-muted">Descrizione</th>
                <th className="px-4 py-3 font-medium text-muted">Creata</th>
                <th className="px-4 py-3 font-medium text-muted">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rules ?? []).map((r: { id: string; rule_key: string; rule_value: string; version: number; description: string; created_at: string }) => (
                <tr key={r.id} className="border-t border-surface-100 dark:border-surface-800">
                  <td className="px-4 py-3 font-mono text-xs font-medium">{r.rule_key}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.rule_value}</td>
                  <td className="px-4 py-3 text-xs">v{r.version}</td>
                  <td className="px-4 py-3 text-xs text-muted max-w-xs truncate">{r.description}</td>
                  <td className="px-4 py-3 text-xs text-muted">{new Date(r.created_at).toLocaleString('it-IT')}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => setHistoryKey(r.rule_key)} className="px-2 py-1 text-xs rounded-lg bg-surface-100 dark:bg-surface-700 hover:opacity-80">
                      📜 Storico
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* History panel */}
      {historyKey && ruleHist?.history && (
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-heading font-semibold">Storico: {historyKey}</h3>
            <button onClick={() => setHistoryKey(null)} className="btn-ghost btn-sm">✕</button>
          </div>
          <div className="space-y-2">
            {ruleHist.history.map((h: { id: string; version: number; rule_value: string; is_active: number; description: string; creator_email: string; created_at: string }) => (
              <div key={h.id} className={`flex items-center justify-between text-sm py-2 px-3 rounded-lg ${h.is_active ? 'bg-primary-50 dark:bg-primary-500/10' : 'bg-surface-50 dark:bg-surface-800'}`}>
                <span className="font-mono text-xs">v{h.version}</span>
                <span className="font-mono text-xs flex-1 mx-3">{h.rule_value}</span>
                <span className="text-muted text-xs">{h.creator_email}</span>
                {!h.is_active && (
                  <button onClick={() => rollback.mutate({ key: historyKey, version: h.version })} className="ml-2 px-2 py-1 text-xs rounded-lg bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400 hover:opacity-80">
                    ↩ Rollback
                  </button>
                )}
                {h.is_active === 1 && <span className="ml-2 text-xs text-green-500">✓ attiva</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   ADMIN REFERRAL TAB
   ═══════════════════════════════════════════════ */
function AdminReferralTab() {
  const { data, isLoading } = useQuery({ queryKey: ['admin-referral-stats'], queryFn: () => api.get('/admin/referral/stats').then(r => r.data) });
  if (isLoading) return <LoadingSpinner />;
  const s = data ?? {};
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {[
          { icon: '👥', label: 'Utenti con codice', value: s.total_users_with_code },
          { icon: '📨', label: 'Inviti totali',     value: s.total_invites },
          { icon: '�️', label: 'Entrati',          value: s.total_joined },
          { icon: '⭐', label: 'Punti emessi',      value: s.total_points_issued },
          { icon: '💱', label: 'Conversioni (€)',   value: s.total_conversions_eur ? `€${Number(s.total_conversions_eur).toFixed(2)}` : '€0.00' },
        ].map(k => (
          <div key={k.label} className="card p-4 text-center">
            <div className="text-2xl mb-1">{k.icon}</div>
            <div className="text-xl font-bold text-heading">{k.value ?? '—'}</div>
            <div className="text-xs text-muted">{k.label}</div>
          </div>
        ))}
      </div>
      {s.top_referrers?.length > 0 && (
        <div className="card p-5">
          <h3 className="text-heading font-semibold mb-3">🏆 Top referrer</h3>
          <div className="space-y-2">
            {s.top_referrers.map((r: { full_name: string; email: string; total_points: number; invite_count: number }, i: number) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-surface-100 dark:border-surface-800 last:border-0 py-2">
                <span className="font-medium text-heading">{r.full_name}</span>
                <span className="text-muted text-xs">{r.email}</span>
                <span className="font-mono">{r.invite_count} inv.</span>
                <span className="font-bold text-primary-600 dark:text-primary-400">{r.total_points} pts</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   ADMIN SUBSCRIPTIONS TAB
   ═══════════════════════════════════════════════ */
function AdminSubscriptionsTab() {
  const { data, isLoading } = useQuery({ queryKey: ['admin-subs-stats'], queryFn: () => api.get('/admin/subscriptions/stats').then(r => r.data) });
  if (isLoading) return <LoadingSpinner />;
  const s = data ?? {};
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { icon: '✅', label: 'Attive', value: s.active },
          { icon: '❌', label: 'Cancellate', value: s.cancelled },
          { icon: '💰', label: 'Revenue mensile', value: s.monthly_revenue ? `€${Number(s.monthly_revenue).toFixed(2)}` : '€0.00' },
          { icon: '📊', label: 'Totale storico', value: s.total_subscriptions },
        ].map(k => (
          <div key={k.label} className="card p-4 text-center">
            <div className="text-2xl mb-1">{k.icon}</div>
            <div className="text-xl font-bold text-heading">{k.value ?? '—'}</div>
            <div className="text-xs text-muted">{k.label}</div>
          </div>
        ))}
      </div>
      {/* Per piano */}
      {s.by_plan && (
        <div className="card p-5">
          <h3 className="text-heading font-semibold mb-3">Per piano</h3>
          <div className="flex flex-wrap gap-3">
            {s.by_plan.map((p: { plan: string; count: number }) => (
              <div key={p.plan} className="bg-surface-100 dark:bg-surface-800 rounded-lg px-4 py-2">
                <span className="font-mono text-sm capitalize">{p.plan}</span>
                <span className="ml-2 font-bold">{p.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Revenue split */}
      {s.revenue_split && (
        <div className="card p-5">
          <h3 className="text-heading font-semibold mb-3">Revenue split</h3>
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full" />
              <span className="text-sm text-muted">Fondo: €{Number(s.revenue_split.fund ?? 0).toFixed(2)}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full" />
              <span className="text-sm text-muted">Pool: €{Number(s.revenue_split.pool ?? 0).toFixed(2)}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-amber-500 rounded-full" />
              <span className="text-sm text-muted">Fee: €{Number(s.revenue_split.fee ?? 0).toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   ADMIN SIMULATOR TAB
   ═══════════════════════════════════════════════ */
function AdminSimulatorTab() {
  return <SimulatorPage />;
}

/* ═══════════════════════════════════════════════
   ADMIN CHARTS TAB (FASE 6: Grafici avanzati)
   ═══════════════════════════════════════════════ */
function AdminChartsTab() {
  const { data: recycling } = useQuery({ queryKey: ['analytics-recycling'], queryFn: () => api.get('/analytics/recycling').then(r => r.data) });
  const { data: boxSizing } = useQuery({ queryKey: ['analytics-box-sizing'], queryFn: () => api.get('/analytics/box-sizing').then(r => r.data) });
  const { data: distribution } = useQuery({ queryKey: ['analytics-distribution'], queryFn: () => api.get('/analytics/distribution').then(r => r.data) });

  return (
    <div className="space-y-6">
      <AnalyticsCharts />

      {/* Recycling stats */}
      {recycling && (
        <div className="card">
          <h3 className="text-lg font-semibold mb-3 text-surface-900 dark:text-white">🔄 Ricircolo Progressivo</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="card-flat text-center">
              <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{recycling.avg_rate}%</p>
              <p className="text-xs text-muted">Tasso medio</p>
            </div>
            <div className="card-flat text-center">
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">€{recycling.total_recycled}</p>
              <p className="text-xs text-muted">Totale riciclato</p>
            </div>
            <div className="card-flat text-center">
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{recycling.high_recyclers}</p>
              <p className="text-xs text-muted">Alto ricircolo (≥50%)</p>
            </div>
          </div>
        </div>
      )}

      {/* Dynamic Box Sizing */}
      {boxSizing && (
        <div className="card">
          <h3 className="text-lg font-semibold mb-3 text-surface-900 dark:text-white">📐 Box Sizing Dinamico</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            {Object.entries(boxSizing.box_sizes || {}).map(([key, val]: [string, any]) => (
              <div key={key} className={`card-flat text-center ${boxSizing.optimal_size?.recommended_size === key ? 'ring-2 ring-primary-500' : ''}`}>
                <p className="text-lg font-bold text-surface-900 dark:text-white capitalize">{key}</p>
                <p className="text-sm text-muted">{val.slots} posti ({val.donors}D + {val.bridges}B + 1R)</p>
                <p className="text-xs text-muted mt-1">Payout ×{val.payout_multiplier}</p>
                {boxSizing.optimal_size?.recommended_size === key && (
                  <span className="inline-block mt-1 px-2 py-0.5 bg-primary-500/10 text-primary-600 dark:text-primary-400 rounded text-xs font-medium">Consigliato</span>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-muted mt-3">Tempo medio riempimento: {boxSizing.optimal_size?.avg_fill_time_days ?? '—'} giorni</p>
        </div>
      )}

      {/* Distribution model */}
      {distribution && (
        <div className="card">
          <h3 className="text-lg font-semibold mb-3 text-surface-900 dark:text-white">💰 Distribuzione Multi-Livello</h3>
          <div className="grid sm:grid-cols-4 gap-3">
            {Object.entries(distribution.distribution_model || {}).map(([key, val]: [string, any]) => (
              <div key={key} className="card-flat text-center">
                <p className="text-lg font-bold text-surface-900 dark:text-white">{val}</p>
                <p className="text-xs text-muted capitalize">{key}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   ADMIN I18N TAB (FASE 6: Traduzioni)
   ═══════════════════════════════════════════════ */
function AdminI18nTab() {
  const { data: stats, isLoading } = useQuery({ queryKey: ['i18n-stats'], queryFn: () => api.get('/i18n/admin/stats').then(r => r.data) });

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="card">
        <h3 className="text-lg font-semibold mb-4 text-surface-900 dark:text-white">🌐 Statistiche Traduzioni</h3>

        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <div className="card-flat text-center">
            <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{stats?.supported_languages?.length ?? 0}</p>
            <p className="text-xs text-muted">Lingue supportate</p>
          </div>
          <div className="card-flat text-center">
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{stats?.default_language?.toUpperCase() ?? 'IT'}</p>
            <p className="text-xs text-muted">Lingua predefinita</p>
          </div>
          <div className="card-flat text-center">
            <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats?.total_overrides ?? 0}</p>
            <p className="text-xs text-muted">Override personalizzati</p>
          </div>
        </div>

        {/* Per language */}
        <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-3">Stato per lingua</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-200 dark:border-surface-700">
                <th className="text-left py-2 text-muted">Lingua</th>
                <th className="text-center py-2 text-muted">Chiavi</th>
                <th className="text-center py-2 text-muted">File</th>
              </tr>
            </thead>
            <tbody>
              {stats?.per_language && Object.entries(stats.per_language).map(([lang, info]: [string, any]) => (
                <tr key={lang} className="border-b border-surface-100 dark:border-surface-800">
                  <td className="py-2 font-medium text-surface-900 dark:text-white">{lang.toUpperCase()}</td>
                  <td className="py-2 text-center">{info.total_keys}</td>
                  <td className="py-2 text-center">{info.has_file ? '✅' : '❌'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   ADMIN SYSTEM TAB (FASE 6: Pausa/Sistema)
   ═══════════════════════════════════════════════ */
function AdminSystemTab() {
  const qc = useQueryClient();
  const { data: status, isLoading } = useQuery({ queryKey: ['system-status'], queryFn: () => api.get('/analytics/system-status').then(r => r.data) });
  const togglePause = useMutation({
    mutationFn: (activate: boolean) => api.post('/analytics/system-pause', { activate }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['system-status'] }),
  });
  const checkPause = useMutation({
    mutationFn: () => api.post('/analytics/check-pause'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['system-status'] }),
  });

  if (isLoading) return <LoadingSpinner />;

  const isPaused = status?.is_paused ?? false;

  return (
    <div className="space-y-6">
      {/* Status card */}
      <div className={`card ${isPaused ? 'ring-2 ring-amber-500' : ''}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{isPaused ? '⚠️' : '✅'}</span>
            <div>
              <h3 className="text-lg font-semibold text-surface-900 dark:text-white">
                Stato Sistema: {status?.status ?? 'UNKNOWN'}
              </h3>
              <p className="text-sm text-muted">
                {isPaused
                  ? 'SYSTEM_SLOWDOWN — Box esistenti continuano a riempirsi, nessun nuovo box'
                  : 'Sistema funziona normalmente'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => checkPause.mutate()}
              disabled={checkPause.isPending}
              className="btn-ghost btn-sm"
            >
              🔍 Check
            </button>
            <button
              onClick={() => togglePause.mutate(!isPaused)}
              disabled={togglePause.isPending}
              className={`btn-sm ${isPaused ? 'btn-primary' : 'bg-amber-500 text-white hover:bg-amber-600'}`}
            >
              {isPaused ? '▶️ Riprendi' : '⏸️ Pausa'}
            </button>
          </div>
        </div>

        {/* Metrics */}
        {status?.metrics && (
          <div className="grid sm:grid-cols-3 md:grid-cols-5 gap-3">
            <div className="card-flat text-center">
              <p className="text-lg font-bold text-surface-900 dark:text-white">{status.metrics.open_boxes}</p>
              <p className="text-xs text-muted">Box aperti</p>
            </div>
            <div className="card-flat text-center">
              <p className="text-lg font-bold text-surface-900 dark:text-white">{(status.metrics.fill_rate * 100).toFixed(1)}%</p>
              <p className="text-xs text-muted">Fill rate</p>
            </div>
            <div className="card-flat text-center">
              <p className="text-lg font-bold text-surface-900 dark:text-white">{status.metrics.recent_joins_7d}</p>
              <p className="text-xs text-muted">Ingressi 7gg</p>
            </div>
            <div className="card-flat text-center">
              <p className="text-lg font-bold text-surface-900 dark:text-white">{status.metrics.recent_completions}</p>
              <p className="text-xs text-muted">Completati 7gg</p>
            </div>
            <div className="card-flat text-center">
              <p className="text-lg font-bold text-surface-900 dark:text-white">{status.thresholds?.max_open_boxes ?? 50}</p>
              <p className="text-xs text-muted">Max box aperti</p>
            </div>
          </div>
        )}
      </div>

      {/* Thresholds */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-3 text-surface-900 dark:text-white">⚙️ Soglie Configurate</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="card-flat text-center">
            <p className="text-lg font-bold text-amber-600 dark:text-amber-400">{((status?.thresholds?.fill_rate_pause ?? 0.3) * 100).toFixed(0)}%</p>
            <p className="text-xs text-muted">Soglia pausa (fill rate)</p>
          </div>
          <div className="card-flat text-center">
            <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{((status?.thresholds?.fill_rate_resume ?? 0.6) * 100).toFixed(0)}%</p>
            <p className="text-xs text-muted">Soglia ripresa</p>
          </div>
          <div className="card-flat text-center">
            <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{status?.thresholds?.max_open_boxes ?? 50}</p>
            <p className="text-xs text-muted">Max box aperti</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   NOTIFICATIONS TAB (FASE 7)
   ═══════════════════════════════════════════════ */
function AdminNotificationsTab() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['admin-notification-stats'],
    queryFn: () => api.get('/notifications/admin/stats').then((r) => r.data),
  });
  const queryClient = useQueryClient();
  const broadcastMut = useMutation({
    mutationFn: (body: { title: string; body: string }) => api.post('/notifications/admin/broadcast', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-notification-stats'] }),
  });
  const [bcTitle, setBcTitle] = useState('');
  const [bcBody, setBcBody] = useState('');

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-bold text-surface-900 dark:text-white">🔔 Notifiche — Statistiche</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Totale', value: stats?.total ?? 0 },
          { label: 'Non lette', value: stats?.unread ?? 0 },
          { label: 'Oggi', value: stats?.today ?? 0 },
          { label: 'Read rate', value: `${stats?.read_rate ?? 0}%` },
        ].map((m) => (
          <div key={m.label} className="bg-white dark:bg-surface-900 p-4 rounded-2xl border border-surface-200 dark:border-surface-700 text-center">
            <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{m.value}</p>
            <p className="text-xs text-surface-500 mt-1">{m.label}</p>
          </div>
        ))}
      </div>

      {(stats?.by_type ?? []).length > 0 && (
        <div className="bg-white dark:bg-surface-900 p-4 rounded-2xl border border-surface-200 dark:border-surface-700">
          <h3 className="font-semibold text-sm mb-3 text-surface-900 dark:text-white">Per tipo</h3>
          <div className="space-y-2">
            {stats.by_type.map((t: any) => (
              <div key={t.type} className="flex justify-between text-sm">
                <span className="text-surface-600 dark:text-surface-400">{t.type}</span>
                <span className="font-medium text-surface-900 dark:text-white">{t.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-surface-900 p-4 rounded-2xl border border-surface-200 dark:border-surface-700">
        <h3 className="font-semibold text-sm mb-3 text-surface-900 dark:text-white">📢 Broadcast</h3>
        <input type="text" value={bcTitle} onChange={(e) => setBcTitle(e.target.value)} placeholder="Titolo" className="w-full mb-2 px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-sm text-surface-900 dark:text-white" />
        <textarea value={bcBody} onChange={(e) => setBcBody(e.target.value)} placeholder="Messaggio..." className="w-full mb-2 px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-sm text-surface-900 dark:text-white" rows={3} />
        <button
          onClick={() => { broadcastMut.mutate({ title: bcTitle, body: bcBody }); setBcTitle(''); setBcBody(''); }}
          disabled={!bcTitle.trim() || !bcBody.trim() || broadcastMut.isPending}
          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-sm font-medium disabled:opacity-50"
        >
          {broadcastMut.isPending ? 'Invio...' : 'Invia a tutti'}
        </button>
        {broadcastMut.isSuccess && <p className="text-xs text-emerald-500 mt-2">Broadcast inviato!</p>}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   CHAT TAB (FASE 7)
   ═══════════════════════════════════════════════ */
function AdminChatTab() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['admin-chat-stats'],
    queryFn: () => api.get('/chat/admin/stats').then((r) => r.data),
  });

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-bold text-surface-900 dark:text-white">💬 Chat — Statistiche</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: 'Room totali', value: stats?.total_rooms ?? 0 },
          { label: 'Messaggi totali', value: stats?.total_messages ?? 0 },
          { label: 'Messaggi oggi', value: stats?.messages_today ?? 0 },
        ].map((m) => (
          <div key={m.label} className="bg-white dark:bg-surface-900 p-4 rounded-2xl border border-surface-200 dark:border-surface-700 text-center">
            <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{m.value}</p>
            <p className="text-xs text-surface-500 mt-1">{m.label}</p>
          </div>
        ))}
      </div>

      {(stats?.rooms_by_type ?? []).length > 0 && (
        <div className="bg-white dark:bg-surface-900 p-4 rounded-2xl border border-surface-200 dark:border-surface-700">
          <h3 className="font-semibold text-sm mb-3 text-surface-900 dark:text-white">Room per tipo</h3>
          <div className="space-y-2">
            {stats.rooms_by_type.map((t: any) => (
              <div key={t.type} className="flex justify-between text-sm">
                <span className="text-surface-600 dark:text-surface-400">{t.type === 'box' ? '📦 Box' : '🎧 Support'}</span>
                <span className="font-medium text-surface-900 dark:text-white">{t.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   GAMIFICATION TAB (FASE 7)
   ═══════════════════════════════════════════════ */
function AdminGamificationTab() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['admin-gamification-stats'],
    queryFn: () => api.get('/gamification/admin/stats').then((r) => r.data),
  });

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-bold text-surface-900 dark:text-white">🏆 Gamification — Statistiche</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Badge assegnati', value: stats?.total_badges_awarded ?? 0 },
          { label: 'Utenti con badge', value: stats?.unique_users ?? 0 },
          { label: 'XP distribuiti', value: stats?.total_xp_distributed ?? 0 },
          { label: 'Badge disponibili', value: stats?.available_badges ?? 0 },
        ].map((m) => (
          <div key={m.label} className="bg-white dark:bg-surface-900 p-4 rounded-2xl border border-surface-200 dark:border-surface-700 text-center">
            <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{m.value}</p>
            <p className="text-xs text-surface-500 mt-1">{m.label}</p>
          </div>
        ))}
      </div>

      {(stats?.top_badges ?? []).length > 0 && (
        <div className="bg-white dark:bg-surface-900 p-4 rounded-2xl border border-surface-200 dark:border-surface-700">
          <h3 className="font-semibold text-sm mb-3 text-surface-900 dark:text-white">Top Badge</h3>
          <div className="space-y-2">
            {stats.top_badges.map((b: any) => (
              <div key={b.badge_id} className="flex justify-between text-sm">
                <span className="text-surface-600 dark:text-surface-400">{b.badge_name}</span>
                <span className="font-medium text-surface-900 dark:text-white">{b.count}x</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {Object.keys(stats?.level_distribution ?? {}).length > 0 && (
        <div className="bg-white dark:bg-surface-900 p-4 rounded-2xl border border-surface-200 dark:border-surface-700">
          <h3 className="font-semibold text-sm mb-3 text-surface-900 dark:text-white">Distribuzione livelli</h3>
          <div className="flex gap-2 flex-wrap">
            {Object.entries(stats.level_distribution).map(([level, count]: any) => (
              <div key={level} className="px-3 py-1.5 rounded-xl bg-primary-50 dark:bg-primary-500/10 text-xs font-medium text-primary-700 dark:text-primary-400">
                Lv.{level}: {count} utenti
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   ACTIVITY TAB (FASE 7)
   ═══════════════════════════════════════════════ */
function AdminActivityTab() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['admin-activity-stats'],
    queryFn: async () => {
      try {
        // Usa endpoint gamification stats + notification stats come proxy
        const [notifRes, gameRes] = await Promise.all([
          api.get('/notifications/admin/stats').then((r) => r.data).catch(() => null),
          api.get('/gamification/admin/stats').then((r) => r.data).catch(() => null),
        ]);
        return { notifications: notifRes, gamification: gameRes };
      } catch {
        return { notifications: null, gamification: null };
      }
    },
  });

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-bold text-surface-900 dark:text-white">📋 Attività — Panoramica</h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Notifiche totali', value: stats?.notifications?.total ?? 0 },
          { label: 'Notifiche oggi', value: stats?.notifications?.today ?? 0 },
          { label: 'Badge assegnati', value: stats?.gamification?.total_badges_awarded ?? 0 },
          { label: 'XP distribuiti', value: stats?.gamification?.total_xp_distributed ?? 0 },
        ].map((m) => (
          <div key={m.label} className="bg-white dark:bg-surface-900 p-4 rounded-2xl border border-surface-200 dark:border-surface-700 text-center">
            <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{m.value}</p>
            <p className="text-xs text-surface-500 mt-1">{m.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-white dark:bg-surface-900 p-4 rounded-2xl border border-surface-200 dark:border-surface-700">
        <h3 className="font-semibold text-sm mb-3 text-surface-900 dark:text-white">Stato servizi FASE 7</h3>
        <div className="space-y-2">
          {[
            { name: 'NotificationService', status: stats?.notifications ? 'Attivo' : 'N/A' },
            { name: 'ChatService', status: 'Attivo' },
            { name: 'GamificationService', status: stats?.gamification ? 'Attivo' : 'N/A' },
            { name: 'ActivityService', status: 'Attivo' },
          ].map((s) => (
            <div key={s.name} className="flex justify-between text-sm">
              <span className="text-surface-600 dark:text-surface-400">{s.name}</span>
              <span className={`font-medium ${s.status === 'Attivo' ? 'text-emerald-500' : 'text-surface-400'}`}>
                {s.status === 'Attivo' ? '🟢' : '⚪'} {s.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   SHARED
   ═══════════════════════════════════════════════ */
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="w-8 h-8 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
    </div>
  );
}
